// @flow

// Types
import type { FieldBorderOperation } from 'fc-operator-fieldborder/types'

// Utils
import _find from 'lodash/find'
import _forEach from 'lodash/forEach'
import _reduce from 'lodash/reduce'
import _reverse from 'lodash/reverse'
import _last from 'lodash/last'
import _split from 'lodash/split'
import _values from 'lodash/values'
import _map from 'lodash/map'
import _omit from 'lodash/omit'
import _indexOf from 'lodash/indexOf'
import _size from 'lodash/size'
import traverse from 'fc-utils/traverse'

// Libs
import React, { Component } from 'react'
import Spinner from 'react-spinkit'

// Localization
import { Errors } from 'hfty-components-errors'
import { FormattedMessage } from 'react-intl'
import messages from '../messages'

// Components
import FormField from '@react-mdc/form-field'
import Dropzone from 'react-dropzone'
import CloseIcon from 'fc-icons/src/Close'

// Constants
import { PROP_SHAPE_DATA } from 'fc-model-fieldborder'
import { PROP_ASSET_ID } from 'fc-model-assets'

// Styles
import { black, dark, primary, error } from 'hfty-styles/src/colors'
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
  },

  submissionOverlay: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: 3000,
    top: 0,
    bottom: 0,
    right: 0,
    backgroundColor: black(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fieldInfo: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 5,
  },
  dropZoneText: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 12,
    color: black(),
  },
  underlineText: {
    textDecoration: 'underline',
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 5,
    color: dark(),
    fontSize: '0.9em',
  },
  fileDiv: {
    flex: '1 1 auto',
  },
  dropZone: {
    width: '100%',
    minHeight: 50,
    borderWidth: 2,
    borderStyle: 'dotted',
    borderColor: primary(),
    backgroundColor: primary(0.1),
    padding: 15,
    cursor: 'pointer',
  },
}

type Props = {
  uploadOperation: FieldBorderOperation,
  onChange: Function,
  assets: Array<Asset>,
}

export default class UploadFieldBorder extends Component {
  props: Props

  constructor (props: Props) {
    super(props)

    this.onAddFile = this.onAddFile.bind(this)
    this.onRemoveFile = this.onRemoveFile.bind(this)
    this.listFiles = this.listFiles.bind(this)
    this.renderFieldInfo = this.renderFieldInfo.bind(this)
  }

  renderFieldInfo (): React.Element<any> {
    const { assets } = this.props
    const testUploadOperation = this.props.uploadOperation

    const asset = _find(assets, (asset: Asset): boolean =>
      asset[PROP_ASSET_ID] === testUploadOperation.model.asset) || {}
    const fieldName = asset.label

    return (
      <div style={styles.fieldInfo}>
        <div><FormattedMessage {...messages['uploadBordersFor']} /></div>
        <div>{fieldName}</div>
      </div>
    )
  }

  onAddFile (accepted: Array<Object>, rejected: Array<Object>) {
    // i want to add this file to the operation
    const { uploadOperation } = this.props
    const newShapeData = []
    _forEach(accepted, (file: Object) => { newShapeData.push(file) })
    const shapeData = _reduce(_reverse(uploadOperation.model[PROP_SHAPE_DATA]),
      (allFiles: Array<Object>, file: Object): Array<Object> => {
        const ext = _last(_split(file.name, '.'))
        const similarFile = _find(allFiles, (file: Object): boolean => _last(_split(file.name, '.')) === ext)
        if (similarFile == null) {
          allFiles = [file, ...allFiles]
        }
        return allFiles
      }, newShapeData)
    this.props.onChange({
      ...uploadOperation,
      model: {
        ...uploadOperation.model,
        [PROP_SHAPE_DATA]: shapeData,
      },
    })
  }

  onRemoveFile (fileData: Object, e: Event) {
    const { uploadOperation } = this.props

    this.props.onChange({
      ...uploadOperation,
      model: {
        ...uploadOperation.model,
        [PROP_SHAPE_DATA]: _values(_omit(uploadOperation.model[PROP_SHAPE_DATA],
          _indexOf(uploadOperation.model[PROP_SHAPE_DATA], fileData))),
      },
    })
  }

  renderFileList (): ?React.Element<any> {
    const shapeData = traverse(['model', 'shapeData'])(this.props.uploadOperation)
    if (_size(shapeData)) {
      return (
        <ul>
          {this.listFiles(this.props.uploadOperation.model.shapeData)}
        </ul>
      )
    } else {
      // message user to drop files here
      return (
        <div style={styles.dropZoneText}>
          <div><FormattedMessage {...messages['dragFiles']} /></div>
          <div><FormattedMessage {...messages['click']} /> <span style={styles.underlineText}>
            <FormattedMessage {...messages['here']} /></span><FormattedMessage {...messages['toAdd']} /></div>
        </div>
      )
    }
  }

  listFiles (shapeData: Array<Object>): React.Element<any> {
    return (
      _map(shapeData, (fileData: Object): React.Element<any> => {
        return (
          <li
            key={fileData.name}
            style={styles.fileInfo}>
            <div style={styles.fileDiv}>
              {fileData.name}
            </div>
            <div onClick={(evt: Object) => {
              evt.stopPropagation()
              this.onRemoveFile(fileData, evt)
            }}>
              <CloseIcon />
            </div>
          </li>
        )
      })
    )
  }

  renderDropZone (): React.Element<any> {
    return (
      <Dropzone
        multiple
        onDrop={this.onAddFile}
        acceptedFiles='.shp, .dbf, .shx'
        style={styles.dropZone}
        activeStyle={{
          backgroundColor: primary(0.6),
        }}
        rejectStyle={{
          backgroundColor: error(0.6),
        }}
      >
        {this.renderFileList()}
      </Dropzone>
    )
  }

  render (): React.Element<any> {
    const { uploadOperation } = this.props
    const hasErrors = uploadOperation && uploadOperation.errors
    const isSubmitting = uploadOperation && uploadOperation.submitting
    const triedSubmission = uploadOperation && uploadOperation.triedSubmission

    return (
      <div style={styles.container}>
        {isSubmitting ? (
          <div style={styles.submissionOverlay}>
            <Spinner name='double-bounce' />
          </div>
        ) : <div style={styles.container}>
          <FormField>
            {hasErrors && triedSubmission &&
              <Errors errors={uploadOperation.errors} messages={messages} />}
          </FormField>
          <FormField>
            {this.renderFieldInfo()}
          </FormField>
          <FormField>
            {this.renderDropZone()}
          </FormField>
        </div>
        }
      </div>
    )
  }
}
